import { useEffect, useState } from "react";
import { Check, MessageSquare, X, RotateCcw, Edit, UserRound, Clock, Ban, MoveLeft, Pen } from "lucide-react";
import InnerLayout from "../../components/layouts/inner-layout";
import { approverActionAction, getApprovalAcceptanceAction } from "../../redux/actions/approval-work-flow-action";
import { useParams } from "react-router-dom";
import errorToast from "../../components/helper-components/error-toast";
import { useDispatch } from "react-redux";
import successToast from "../../components/helper-components/success-toast";
import { useNavigate } from "react-router-dom";
function Approval() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [requestData, setRequestData] = useState({});
  const [approvalTimeline, setApprovalTimeline] = useState([]);
  const [showActions, setShowActions] = useState(false);
  const [alertStageIndex, setAlertStageIndex] = useState(null);
  const [comment, setComment] = useState(""); // 🆕 store user comment

  const ButtonClick = (type) => {
    if (!comment.trim()) {
      errorToast("Please enter a comment before proceeding.");
      return;
    }

    const data = {
      request_id: id,
      action: type,
      target_stage_index: alertStageIndex,
      comment: comment.trim(),
    };

    console.log("ButtonClick payload:", data);
    dispatch(approverActionAction(data, onApprovalSuccess, onError));
  };
  const onApprovalSuccess=(data)=>{
    successToast(data?.message||"Success");
    navigate("/approval-work-flow/list");
  };
  const onSuccess = (data) => {
    setRequestData({
      title: data?.approval_flow_name
        ? data.approval_flow_name.charAt(0).toUpperCase() + data.approval_flow_name.slice(1)
        : "",
      description: data?.approval_flow_description
        ? data.approval_flow_description.charAt(0).toUpperCase() + data.approval_flow_description.slice(1)
        : "",
    });

    const stages = data?.stages||[];

    const actionableStages = stages.filter(
      (s) => s.is_actionable === true && s.status === "pending"
    );

    const sorted = actionableStages.sort(
      (a, b) => parseInt(a.stage_index) - parseInt(b.stage_index)
    );

    if (sorted.length > 0) {
      setShowActions(true);
      const firstStage = sorted[0];
      setAlertStageIndex(firstStage.stage_index);
    } else {
      setShowActions(false);
    }

    setApprovalTimeline(stages);
  };

  const onError = (data) => {
    errorToast(data?.data?.message);
  };

  useEffect(() => {
    dispatch(getApprovalAcceptanceAction({ request_id: id }, onSuccess, onError));
  }, [dispatch, id]);

  const getStatusIcon = (status, index) => {
    switch (status) {
      case "approved":
        return <span className="text-sml font-semibold">&#10003;</span>;
      default:
        return <span className="text-sml font-semibold">{index + 1}</span>;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "approved":
        return "bg-[var(--success-color)] text-white";
      case "pending":
        return "bg-primary text-white";
      case "waiting":
        return "bg-[#eaecef] text-[#b0b5bc]";
      default:
        return "bg-[#eaecef] text-[#b0b5bc]";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return "text-[#5ca55e] bg-[#e5fbe9] text-xs px-3 py-1 rounded-full";
      case "pending":
        return "bg-[#f4f7fe] text-primary text-xs px-3 py-1 rounded-full";
      case "waiting":
        return "bg-[#eaecef] text-[#b0b5bc] text-xs px-3 py-1 rounded-full";
      default:
        return "";
    }
  };

  return (
    <div
      className="w-full bg-gray-50 p-4"
      style={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}
    >
      <div className="w-full bg-white">
        <div className="space-y-8">
          {/* Request Details */}
          <div className="rounded-lg shadow-sm border border-[var(--button-border-color)] text-[var(--background-grey)] p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Request Details</h2>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-[var(--placeholder-color)] mb-1">Title</p>
                <p className="text-sm text-gray-900 font-medium">{requestData.title}</p>
              </div>
              <div>
                <p className="text-xs text-[var(--placeholder-color)] mb-1">Description</p>
                <p className="text-sm text-gray-700 leading-relaxed">{requestData.description}</p>
              </div>
            </div>
          </div>

          {/* Approval Timeline */}
          <div className="border border-[var(--button-border-color)] rounded-lg shadow-sm p-4 text-[var(--placeholder-color)]">
            <h1 className="text-sml font-semibold mb-4 text-[#000000]">Approval Timeline</h1>
            <div className="space-y-4">
              {approvalTimeline.map((item, index) => (
                <div key={item.stage_index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] rounded-full flex items-center justify-center ${getStatusBg(
                        item.status
                      )}`}
                    >
                      {getStatusIcon(item.status, index)}
                    </div>

                    {index < approvalTimeline.length - 1 && (
                      <div className="w-0.5 h-full min-h-[60px] bg-gray-200 mt-2"></div>
                    )}
                  </div>

                  <div className="flex-1 pb-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-[#000000]">{item?.role_name || ""}</h3>
                        {item.status === "approved" ? (
                          <div className="w-4 h-4 bg-[var(--success-color)] rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 bg-[#eaecef] text-[#b0b5bc] rounded-full flex items-center justify-center">
                            <Clock className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                      <span className={getStatusBadge(item.status)}>{item.status}</span>
                    </div>

                    <p className="text-xs text-gray-500 mb-1">Stage {item.stage_index}</p>

                    <div className="flex items-center gap-1 text-xs mb-2">
                      <UserRound size={14} />
                      <span>{item?.user_names?.join(", ")}</span>
                    </div>

                    {item.approved_by && item.approved_at && (
                      <p className="text-xs mb-2">
                        Completed by {item.approved_by} on {item.approved_at}
                      </p>
                    )}

                    {item.comments && (
                      <div className="mt-2">
                        <div className="bg-[#f8f9fa] flex items-start gap-2 w-1/2 px-2 py-3 rounded border border-[var(--button-border-color)] shadow-sm">
                          <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-gray-600">{item.comments}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Take Action */}
          {showActions && (
            <>
              <div className="text-[var(--background-grey)] p-4 pb-0">
                <h1 className="text-sml font-semibold mb-4 text-[#000000]">Take Action</h1>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Comments</p>
                  <textarea
                    placeholder="Add your comments here..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full p-3 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none rounded-lg shadow-sm border border-[var(--button-border-color)]"
                    rows={4}
                    maxLength={255}
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {comment?.length || 0}/{255}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <button
                  className="flex items-center gap-2 px-4 py-3 bg-[var(--success-color)] text-white text-sm font-medium rounded transition-colors"
                  onClick={() => ButtonClick("approve")}
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
                <button
                  className="flex items-center gap-2 px-4 py-3 bg-[var(--error-color)] text-white text-sm font-medium rounded transition-colors"
                  onClick={() => ButtonClick("reject")}
                >
                  <Ban className="w-4 h-4" />
                  Reject
                </button>
                <button
                  className="flex items-center gap-2 px-4 py-3 border border-[var(--button-border-color)] text-gray-700 text-sm font-medium rounded transition-colors"
                  onClick={() => ButtonClick("rollback")}
                >
                  <MoveLeft className="w-4 h-4" />
                  Rollback
                </button>
                <button
                  className="flex items-center gap-2 px-4 py-3 border border-[var(--button-border-color)] text-gray-700 text-sm font-medium rounded transition-colors"
                  onClick={() => ButtonClick("edit")}
                  disabled={true}
                >
                  <Pen className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ApprovalAcceptance() {
  return (
    <InnerLayout title="Approval Request View" backBtn={true}>
      <Approval />
    </InnerLayout>
  );
}

export default ApprovalAcceptance;
